import { OrderType } from "@/constants/enum";
import refreshMenu from "@/dataLayer/refreshMenu";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useTranslations } from "next-intl";
import ChevronDownIcon from "../_icons/chevronDownIcon";
import clsx from "clsx";

type OrderTypeDropdownType = {
  orderType: OrderType;
} & {
  setOrderType: any;
  availableOrderTypes: any;
  isDisabled?: boolean;
};

export const OrderTypeDropdown = ({
  orderType,
  setOrderType,
  availableOrderTypes,
  isDisabled = false,
}: OrderTypeDropdownType) => {
  const t = useTranslations("OrderTypeDropDown");
  const enabledOrderTypes = availableOrderTypes.filter(
    (ot: { enabled: any }) => ot.enabled
  );

  const isDropdownDisabled = isDisabled || enabledOrderTypes.length < 2;

  const deliveryOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "DELIVERY"
  );
  const deliveryEnabled = deliveryOrderType?.enabled;

  const curbsideOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "CURBSIDE"
  );
  const curbsideEnabled = curbsideOrderType?.enabled;

  const inStoreOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "PICKUP"
  );
  const inStoreEnabled = inStoreOrderType?.enabled;

  return (
    <Listbox
      value={orderType}
      onChange={(value) => {
        refreshMenu();
        setOrderType(value);
      }}
    >
      {({ open }) => (
        <div className="relative">
          <ListboxButton
            className={clsx(
              "relative w-full h-full leading-6 cursor-pointer text-gray-900 hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-not-allowed disabled:border-[#C7C7C7] disabled:text-[#C7C7C7]",
              {
                "cursor-default": isDropdownDisabled,
              }
            )}
            disabled={isDropdownDisabled}
            data-testid={applyDataTestId(pom.landingPage.orderTypeDropdown)}
            aria-label={`${t("orderType")} ${orderDisplayValue(orderType, t)}`}
          >
            <div
              id="order_type_input"
              className={clsx(
                "peer text-[15px] text-left leading-6 block w-full min-h-[52px] h-full rounded-[4px] border bg-transparent px-2 pt-6 pb-2 pr-10 text-gray-900 tracking-wide whitespace-normal break-words",
                {
                  "border-[#C7C7C7] text-[#C7C7C7]": isDropdownDisabled,
                  "border-black hover:border-black": !isDropdownDisabled,
                }
              )}
            >
              {orderDisplayValue(orderType, t)}
            </div>

            <span
              className={clsx(
                "text-xs leading-[18px] absolute left-2 top-4 origin-[0] -translate-y-2.5 tracking-wide", {
                  "text-[#C7C7C7]": isDropdownDisabled,
                  "text-[#727272]": !isDropdownDisabled,
                }
              )}
            >
              {t("orderType")}
            </span>

            {!isDropdownDisabled && (
              <ChevronDownIcon
                aria-hidden="true"
                className={`cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-transform duration-300 ${
                  open ? "rotate-180" : "rotate-0"
                }`}
              />
            )}
          </ListboxButton>
          <ListboxOptions
            transition
            className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <div className="p-3">
              {inStoreEnabled && (
                <ListboxOption
                  value={OrderType.PICKUP}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 pl-2 pr-1 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50"
                  data-testid={applyDataTestId(
                    pom.orderTypeOption(OrderType.PICKUP)
                  )}
                  aria-label={t("inStore")}
                >
                  <p className="text-left w-full text-[15px]">{t("inStore")}</p>
                </ListboxOption>
              )}
              {curbsideEnabled && (
                <ListboxOption
                  value={OrderType.CURBSIDE}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 pl-2 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50"
                  data-testid={applyDataTestId(
                    pom.orderTypeOption(OrderType.CURBSIDE)
                  )}
                  aria-label={t("curbside")}
                >
                  <p className="text-left w-full text-[15px]">
                    {t("curbside")}
                  </p>
                </ListboxOption>
              )}
              {deliveryEnabled && (
                <ListboxOption
                  value={OrderType.DELIVERY}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 pl-2 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50"
                  data-testid={applyDataTestId(
                    pom.orderTypeOption(OrderType.DELIVERY)
                  )}
                  aria-label={t("delivery")}
                >
                  <p className="text-[15px]">{t("delivery")}</p>
                </ListboxOption>
              )}
            </div>
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};

const orderDisplayValue = (orderType: string, t: any) => {
  if (orderType === OrderType.PICKUP) {
    return t("inStore");
  } else if (orderType === OrderType.CURBSIDE) {
    return t("curbside");
  } else if (orderType === OrderType.DELIVERY) {
    return t("delivery");
  }
  return t("inStore");
};
